import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Clip, SoulEntry, DialogueLine, EntryTake } from '../types';
import { SoulClipItem, sanitizeTakes } from '../components/studio/studioUtils';
import { fetchSavedClips, deleteSavedClip, getTempVaultClips } from '../services/curatorService';
import { listEntries, fetchEntryByClipId, deleteEntry } from '../services/entryService';

interface UseStudioVaultOptions {
  currentClip: Clip | null;
  activeEntry: SoulEntry | null;
  userName?: string;
  onSelectClip?: (clip: Clip | null) => void;
  onBeforeSelectClip?: () => void;
  onEntryUpdated?: (entry: SoulEntry) => void;
  lastFetchedClipIdRef?: React.MutableRefObject<string | null>;
  setActiveEntry: (entry: SoulEntry | null) => void;
  setDialogueLines: (lines: DialogueLine[]) => void;
  dialogueLinesRef: React.MutableRefObject<DialogueLine[]>;
  setTakes: (takes: EntryTake[]) => void;
  setHasStems: (hasStems: boolean) => void;
  setDuration: (duration: number) => void;
  setPerformerName: (name: string) => void;
  addTelemetryLog?: (message: string, type: 'info' | 'pending' | 'success' | 'warn' | 'dispatch') => void;
}

export function useStudioVault({
  currentClip,
  activeEntry,
  userName,
  onSelectClip,
  onBeforeSelectClip,
  onEntryUpdated,
  lastFetchedClipIdRef,
  setActiveEntry,
  setDialogueLines,
  dialogueLinesRef,
  setTakes,
  setHasStems,
  setDuration,
  setPerformerName,
  addTelemetryLog
}: UseStudioVaultOptions) {
  const [isVaultOpen, setIsVaultOpen] = useState<boolean>(() => {
    return sessionStorage.getItem('soulsync_vault_open') === 'true';
  });
  const [vaultClips, setVaultClips] = useState<SoulClipItem[]>(() => {
    try {
      const tempClips = !userName ? getTempVaultClips() : [];
      return tempClips.map((c) => ({
        id: c.id,
        title: c.title,
        duration: c.duration,
        trimStart: c.trimStart,
        trimEnd: c.trimEnd,
        posterUrl: c.posterUrl || '',
        videoUrl: c.videoUrl || `/api/media/clips/${c.id}.mp4`,
        sourceUrl: c.sourceUrl || '',
        audioUrl: c.audioUrl,
        isPreset: false,
        takeCount: 0,
        dialogueCount: 0,
        hasStems: Boolean((c as any).hasStems === true),
        entry: null
      }));
    } catch {
      return [];
    }
  });
  const [savedClips, setSavedClips] = useState<Clip[]>([]);
  const [isLoadingVault, setIsLoadingVault] = useState<boolean>(false);
  const isRefreshingVaultRef = useRef<boolean>(false);
  const isSelectingClipRef = useRef<boolean>(false);
  const [deletingClipId, setDeletingClipId] = useState<string | null>(null);
  const [vaultFilter, setVaultFilter] = useState<'all' | 'takes' | 'stems' | 'presets'>('all');
  const [vaultSearch, setVaultSearch] = useState<string>('');

  // When transitioning to a fresh capture or when a clip is being baked, ensure the vault is closed
  useEffect(() => {
    if (currentClip && (currentClip.pendingFile || (currentClip as any).hasStems === false || sessionStorage.getItem('soulsync_vault_open') === 'false')) {
      setIsVaultOpen(false);
      try {
        sessionStorage.setItem('soulsync_vault_open', 'false');
      } catch (_) {}
    }
  }, [currentClip?.id]);

  useEffect(() => {
    const handleCloseVault = () => {
      setIsVaultOpen(false);
      try {
        sessionStorage.setItem('soulsync_vault_open', 'false');
      } catch (_) {}
    };
    window.addEventListener('soulsync:close_vault', handleCloseVault);
    return () => {
      window.removeEventListener('soulsync:close_vault', handleCloseVault);
    };
  }, []);

  const refreshSoulClips = useCallback(async () => {
    if (isRefreshingVaultRef.current) return;
    isRefreshingVaultRef.current = true;
    setIsLoadingVault(true);
    try {
      const [clips, entries] = await Promise.all([
        fetchSavedClips(userName?.trim() || undefined),
        userName?.trim() ? listEntries(userName.trim()) : Promise.resolve([])
      ]);

      setSavedClips(clips || []);

      const entryMap = new Map<string, SoulEntry>();
      for (const e of entries) {
        if (e.clipId && !entryMap.has(e.clipId)) {
          entryMap.set(e.clipId, e);
        }
        const cleanId = (e.clipId || '').replace(/^entry-/, '');
        if (cleanId && !entryMap.has(cleanId)) {
          entryMap.set(cleanId, e);
        }
      }

      const customItems: SoulClipItem[] = (clips || []).map((c: any) => {
        const matchingEntry = entryMap.get(c.id) || entryMap.get(`entry-${c.id}`) || entryMap.get((c.id || '').replace(/^entry-/, ''));
        const ytId = c.parent_clip_id || c.parentClipId || c.youtubeId || '';
        const poster =
          c.posterUrl ||
          c.poster_url ||
          c.thumbnailUrl ||
          (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : `/api/media/clips/${c.id}.jpg`);
        const trimStart = c.trim_start !== undefined ? c.trim_start : (c.trimStart || 0);
        const trimEnd = c.trim_end !== undefined ? c.trim_end : (c.trimEnd || c.duration || 15);
        const duration = c.duration || Math.round((trimEnd - trimStart) * 10) / 10;
        const takesCount =
          matchingEntry?.takeCount !== undefined
            ? matchingEntry.takeCount
            : (matchingEntry?.takes?.length || 0);
        const dialogueCount = matchingEntry?.dialogueLines?.length || 0;
        const stemsReady = Boolean(
          c.isPreset === true ||
          c.hasStems !== false ||
          (matchingEntry && (matchingEntry as any).hasStems === true) ||
          Boolean(matchingEntry?.vocalsUrl && matchingEntry?.bgmUrl) ||
          Boolean(c.vocalsUrl || c.vocals_url)
        );

        return {
          id: c.id,
          title: c.title || `Captured Scene (${c.id})`,
          duration,
          trimStart,
          trimEnd,
          posterUrl: poster,
          videoUrl: c.videoUrl || c.video_url || `/api/media/clips/${c.id}.mp4`,
          sourceUrl: c.sourceUrl || c.source_url || (ytId ? `https://www.youtube.com/watch?v=${ytId}` : ''),
          audioUrl: c.audioUrl || c.audio_url || `/api/media/stems/${c.id}/bgm.mp3`,
          bgmUrl: c.bgmUrl || c.audioUrl || c.audio_url || `/api/media/stems/${c.id}/bgm.mp3`,
          vocalsUrl: c.vocalsUrl || c.vocals_url || `/api/media/stems/${c.id}/vocals.mp3`,
          r2Key: c.r2_key || c.r2Key || `clips/${c.id}.json`,
          createdAt: c.created_at || c.createdAt || Date.now(),
          parentClipId: ytId,
          isPreset: false,
          takeCount: takesCount,
          dialogueCount: dialogueCount,
          hasStems: stemsReady,
          latestPerformer: matchingEntry?.performerName,
          entry: matchingEntry || null
        };
      });

      setVaultClips(customItems);
    } catch (err) {
      console.warn('Error refreshing SoulClips vault:', err);
    } finally {
      isRefreshingVaultRef.current = false;
      setIsLoadingVault(false);
    }
  }, [userName]);

  useEffect(() => {
    refreshSoulClips();
    const handleVaultUpdated = () => {
      refreshSoulClips();
    };
    window.addEventListener('soulsync:vault_updated', handleVaultUpdated);
    return () => {
      window.removeEventListener('soulsync:vault_updated', handleVaultUpdated);
    };
  }, [refreshSoulClips]);

  const handleSelectSoulClip = useCallback(
    async (clipItem: SoulClipItem) => {
      // Prevent concurrent selections
      if (isSelectingClipRef.current) return;
      isSelectingClipRef.current = true;

      try {
        // 1. Immediately twirl closed the vault drawer upon selecting a clip
        setIsVaultOpen(false);
        sessionStorage.setItem('soulsync_vault_open', 'false');

        if (onBeforeSelectClip) {
          onBeforeSelectClip();
        }

        const parentId = clipItem.parentClipId || '';
        const mappedClip: Clip = {
          id: clipItem.id,
          title: clipItem.title,
          duration: clipItem.duration,
          trimStart: clipItem.trimStart,
          trimEnd: clipItem.trimEnd,
          posterUrl: clipItem.posterUrl,
          thumbnailUrl: clipItem.posterUrl,
          videoUrl: clipItem.videoUrl || `/api/media/clips/${clipItem.id}.mp4`,
          sourceUrl: clipItem.sourceUrl || clipItem.videoUrl || '',
          audioUrl: clipItem.audioUrl || `/api/media/stems/${clipItem.id}/bgm.mp3`,
          vocalsUrl: clipItem.vocalsUrl || `/api/media/stems/${clipItem.id}/vocals.mp3`,
          category: clipItem.isPreset ? 'Preset Scene' : 'SoulClip R2',
          youtubeId: parentId,
          isEditable: clipItem.isEditable,
          hasStems: clipItem.hasStems !== false,
          playbackMode: 'html5'
        } as any;

        if (onSelectClip) {
          onSelectClip(mappedClip);
        }

        // If entry was preloaded on the item and already has takes, use it
        if (clipItem.entry && clipItem.entry.takes && clipItem.entry.takes.length > 0) {
          const e = clipItem.entry;
          if (lastFetchedClipIdRef) {
            lastFetchedClipIdRef.current = clipItem.id;
          }
          setActiveEntry(e);
          if (onEntryUpdated) {
            onEntryUpdated(e);
          }
          if (e.dialogueLines && e.dialogueLines.length > 0) {
            const enriched = e.dialogueLines.map((l, idx) => ({
              ...l,
              id: l.id || `line-${idx + 1}`,
              rawStart: l.rawStart !== undefined ? l.rawStart : (l.start ?? l.startTime ?? 0),
              rawEnd: l.rawEnd !== undefined ? l.rawEnd : (l.end ?? l.endTime ?? (e.duration || 15))
            }));
            setDialogueLines(enriched);
            dialogueLinesRef.current = enriched;
          }
          if (clipItem.isEditable === false) {
            setTakes([]);
          } else {
            setTakes(sanitizeTakes(e.takes));
          }
          if (e.vocalsUrl && e.bgmUrl) setHasStems(true);
          else setHasStems(clipItem.hasStems !== false);
          if (e.duration) setDuration(e.duration);
          if (!userName && e.performerName && e.performerName !== 'Artist') setPerformerName(e.performerName);
        } else {
          try {
            if (lastFetchedClipIdRef) {
              lastFetchedClipIdRef.current = `${clipItem.id}:${userName || ''}`;
            }
            const fetched = await fetchEntryByClipId(clipItem.id, userName?.trim() || undefined);
            console.log('[handleSelectSoulClip] fetchEntryByClipId result:', `clipId=${clipItem.id}, fetched=${!!fetched}, takeCount=${fetched?.takes?.length}`);
            if (fetched) {
              setActiveEntry(fetched);
              if (onEntryUpdated) {
                onEntryUpdated(fetched);
              }
              if (fetched.dialogueLines?.length) {
                const enriched = fetched.dialogueLines.map((l, idx) => ({
                  ...l,
                  id: l.id || `line-${idx + 1}`,
                  rawStart: l.rawStart !== undefined ? l.rawStart : (l.start ?? l.startTime ?? 0),
                  rawEnd: l.rawEnd !== undefined ? l.rawEnd : (l.end ?? l.endTime ?? (fetched.duration || 15))
                }));
                setDialogueLines(enriched);
                dialogueLinesRef.current = enriched;
              }
              if (clipItem.isEditable === false) {
                setTakes([]);
              } else if (fetched.takes?.length) {
                setTakes(sanitizeTakes(fetched.takes));
              } else {
                setTakes([]);
              }
              if (fetched.vocalsUrl && fetched.bgmUrl) setHasStems(true);
              else setHasStems(clipItem.hasStems !== false);
              if (fetched.duration) setDuration(fetched.duration);
              if (!userName && fetched.performerName && fetched.performerName !== 'Artist') setPerformerName(fetched.performerName);
            } else {
              console.log('[handleSelectSoulClip] No entry found for clipId:', clipItem.id);
              setActiveEntry(null);
              setTakes([]);
              const dur = clipItem.duration || Math.max(5, clipItem.trimEnd - clipItem.trimStart);
              setDuration(dur);
              setDialogueLines([]);
              setHasStems(clipItem.hasStems !== false);
            }
          } catch (err) {
            console.warn('Error populating clip tools:', err);
          }
        }

        if (addTelemetryLog) {
          addTelemetryLog(`Loaded SoulClip "${clipItem.title}" into Studio tools.`, 'info');
        }
      } finally {
        isSelectingClipRef.current = false;
      }
    },
    [onSelectClip, onBeforeSelectClip, onEntryUpdated, lastFetchedClipIdRef, setActiveEntry, setDialogueLines, dialogueLinesRef, setTakes, setHasStems, setDuration, setPerformerName, addTelemetryLog]
  );

  const handleDeleteSoulClip = useCallback(
    async (e: React.MouseEvent, clipItem: SoulClipItem) => {
      e.stopPropagation();

      const confirmed = window.confirm(
        `Delete "${clipItem.title}" from SoulClips Vault?\n\nThis will remove its capture data, dialogue lines, and recorded takes.`
      );
      if (!confirmed) return;

      setDeletingClipId(clipItem.id);
      if (addTelemetryLog) {
        addTelemetryLog(`Deleting SoulClip "${clipItem.title}" (${clipItem.id})...`, 'info');
      }

      try {
        await deleteSavedClip(clipItem.id);
        if (clipItem.entry?.id) {
          await deleteEntry(clipItem.entry.id);
        }

        setVaultClips((prev) => prev.filter((c) => c.id !== clipItem.id));
        setSavedClips((prev) => prev.filter((c) => c.id !== clipItem.id));
        if (addTelemetryLog) {
          addTelemetryLog(`Successfully deleted "${clipItem.title}" from vault.`, 'success');
        }

        // When the clip is deleted from the vault it should be unloaded from the player window and timeline
        if (currentClip && clipItem.id === currentClip.id) {
          if (onBeforeSelectClip) {
            onBeforeSelectClip();
          }
          setActiveEntry(null);
          setDialogueLines([]);
          if (dialogueLinesRef) {
            dialogueLinesRef.current = [];
          }
          setTakes([]);
          setHasStems(false);
          setDuration(0);
          if (onSelectClip) {
            onSelectClip(null);
          }
          if (addTelemetryLog) {
            addTelemetryLog(`Unloaded deleted clip "${clipItem.title}" from player and timeline.`, 'info');
          }
        }
      } catch (err: any) {
        if (addTelemetryLog) {
          addTelemetryLog(`Failed to delete "${clipItem.title}": ${err.message}`, 'warn');
        }
      } finally {
        setDeletingClipId(null);
      }
    },
    [currentClip, onBeforeSelectClip, onSelectClip, setActiveEntry, setDialogueLines, dialogueLinesRef, setTakes, setHasStems, setDuration, addTelemetryLog]
  );

  const filteredVaultClips = useMemo(() => {
    return vaultClips.filter((c) => {
      if (vaultFilter === 'takes' && (c.takeCount || 0) === 0) return false;
      if (vaultFilter === 'stems' && !c.hasStems) return false;
      if (vaultFilter === 'presets' && !c.isPreset) return false;
      if (vaultSearch.trim()) {
        const q = vaultSearch.toLowerCase().trim();
        return c.title.toLowerCase().includes(q) || c.id.toLowerCase().includes(q);
      }
      return true;
    });
  }, [vaultClips, vaultFilter, vaultSearch]);

  return {
    isVaultOpen,
    setIsVaultOpen,
    vaultClips,
    setVaultClips,
    savedClips,
    isLoadingVault,
    deletingClipId,
    vaultFilter,
    setVaultFilter,
    vaultSearch,
    setVaultSearch,
    filteredVaultClips,
    refreshSoulClips,
    handleSelectSoulClip,
    handleDeleteSoulClip
  };
}
